// Moderation of a creation's texts before publishing: a local filter without network (links, contact details, insults),
// then, if a model is configured, the model's opinion. The game and its scripts stay isolated: only what is displayed is moderated.
import { z } from 'zod/v4';
import type { CreationInput, Issue } from '@gruvetto/contracts';
import type { Llm } from './workshop.js';

const MAX_TEXTS = 120, MAX_LEN = 300;

/** All the visible texts of a creation, bounded in number and length. */
export function creationTexts(c: CreationInput): string[] {
  const out: string[] = [c.name, c.desc, ...c.tags];
  const push = (...t: (string | undefined | null)[]) => { for (const x of t) if (x && x.trim()) out.push(x); };
  switch (c.kind) {
    case 'workout': for (const s of c.content.segs) push(s.text); break;
    case 'plan':
      push(c.content.goal);
      for (const w of c.content.weeks) { push(w.focus); for (const s of w.sessions) push(s.note); }
      for (const w of c.content.workouts) { push(w.name, w.desc); for (const s of w.segs) push(s.text); }
      break;
    case 'track': for (const s of c.content.segments ?? []) push(s.label); break;
    case 'mode': for (const it of c.content.rules.customItems ?? []) push(it.name, it.help); break;
    case 'cosmetic': if (c.content.slot === 'jersey') for (const l of c.content.design.layers) if (l.type === 'text') push(l.text); break;
    case 'game': {
      for (const x of c.content.controls) push(x.label);
      // string literals displayed by the game (comments and code are not displayed)
      const re = /(["'`])((?:\\.|(?!\1)[^\\\n]){3,200})\1/g;
      let m: RegExpExecArray | null, n = 0;
      while ((m = re.exec(c.content.code)) && n++ < 80) if (/[a-zà-ÿ]{3}/i.test(m[2]) && !/^[#.\w-]+$/.test(m[2])) push(m[2]);
      break;
    }
    default: break;
  }
  return out.slice(0, MAX_TEXTS).map(t => t.slice(0, MAX_LEN));
}

/** Comparable form: lowercase, without accents, digits and usual symbols mapped back to letters. */
export function normalize(t: string): string {
  const leet: Record<string, string> = { '0': 'o', '1': 'i', '3': 'e', '4': 'a', '5': 's', '7': 't', '8': 'b', '@': 'a', $: 's', '!': 'i', '€': 'e' };
  return t.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[0134578@$!€]/g, c => leet[c] ?? c).replace(/(.)\1{2,}/g, '$1$1');
}

// Short but real list (insults, hateful or sexual terms), French and English. Compared word by word on the normalised form, repeated letters collapsed.
const BANNED = [
  'connard', 'connasse', 'salope', 'salaud', 'encule', 'enculer', 'pute', 'putain', 'pd', 'pede', 'tapette', 'gouine', 'negre', 'bougnoule', 'bicot', 'youpin', 'chintok',
  'nazi', 'hitler', 'couille', 'chatte', 'nique', 'niquer', 'ntm', 'fdp', 'batard', 'retarde', 'mongol', 'trisomique', 'viol', 'violer',
  'fuck', 'fucking', 'motherfucker', 'shit', 'bitch', 'cunt', 'dick', 'cock', 'pussy', 'whore', 'slut', 'fag', 'faggot', 'nigger', 'nigga', 'retard', 'retarded', 'rape', 'rapist', 'porn', 'porno', 'kys',
  'asshole', 'bastard', 'twat', 'wanker', 'dyke', 'tranny', 'kike', 'spic', 'pedo', 'pedophile',
];
const BANNED_SET = new Set(BANNED.map(normalize).map(w => w.replace(/(.)\1+/g, '$1')));
const LINK = /\b(?:https?:\/\/|www\.)\S+|\b[a-z0-9-]{2,}\.(?:com|fr|be|net|org|io|gg|tv|co|me|xyz|ly|app|ch|eu|de|uk)\b/i;
const EMAIL = /[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}/i;
const PHONE = /(?:\+|00)\d[\d .-]{7,}\d|\b0\d(?:[ .-]?\d{2}){4}\b/;
const HANDLE = /(^|\s)@[a-z0-9_.]{3,}/i;

/** Normalised words; consecutive isolated letters ("c.o.n") are glued back together, but never the inside of a word ("technique"). */
function tokens(t: string): string[] {
  const raw = normalize(t).split(/[^a-z]+/).filter(Boolean), out: string[] = [];
  let run = '';
  for (const w of raw) { if (w.length === 1) { run += w; continue; } if (run.length > 1) out.push(run); run = ''; out.push(w); }
  if (run.length > 1) out.push(run);
  return out.map(w => w.replace(/(.)\1+/g, '$1'));
}

/** Local filter, without network: refuses links, contact details and insults. */
export function quickScreen(texts: string[]): Issue[] {
  const out: Issue[] = [], seen = new Set<string>();
  const add = (message: string) => { if (!seen.has(message)) { seen.add(message); out.push({ level: 'error', message }); } };
  for (const t of texts) {
    if (EMAIL.test(t)) add('No email address in a public creation');
    else if (LINK.test(t)) add('No link or domain name in a public creation');
    if (PHONE.test(t)) add('No phone number in a public creation');
    if (HANDLE.test(t)) add('No social network handle (@…) in a public creation');
    const hit = tokens(t).find(w => BANNED_SET.has(w) || BANNED_SET.has(w.replace(/[sx]$/, ''))); // plurals included
    if (hit) add(`Term refused: "${t.slice(0, 40)}"`);
  }
  return out;
}

const verdict = z.object({ ok: z.boolean(), reasons: z.array(z.string().max(200)).max(5) });
const SYSTEM = `You moderate the texts of the creations published by the players of Gruvetto, a trainer cycling game for all ages. Texts may be in any language, French and English included.
Reject (ok: false) a text that contains: insults or harassment, hateful or discriminatory remarks, sexual content, gratuitous violence, glorification of drugs, the name or mockery of an identifiable real person, a trademark or real professional team, personal data (address, phone, email), advertising or a link.
Accept (ok: true) cycling vocabulary even when harsh ("suffer", "wall", "blow up", "attack", "kill the legs"), good-natured humour, puns and invented names.
reasons: in English, one short sentence per problem found, empty if all is well.`;

/** The model's opinion on a creation's texts. Throws if the model does not answer (up to the caller to decide). */
export async function moderate(llm: Llm, texts: string[]): Promise<{ ok: boolean; reasons: string[] }> {
  if (!texts.length) return { ok: true, reasons: [] };
  const r = await llm.complete({ system: SYSTEM, messages: [{ role: 'user', content: `Texts to check (one per line):\n${texts.map(t => `- ${t.replace(/\n/g, ' ')}`).join('\n')}` }], schema: verdict, effort: 'low', maxTokens: 2000 });
  const v = verdict.safeParse(r.data);
  if (!v.success) throw new Error('Unreadable moderation verdict');
  return { ok: v.data.ok, reasons: v.data.ok ? [] : v.data.reasons.length ? v.data.reasons : ['Content rejected by moderation'] };
}
